import { Avatar } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import ObjectiveAttributeChip from './ObjectiveAttributeChip';
import ObjectiveParticipantChip from './ObjectiveParticipantChip';
import { participantAttributeKey } from '../participantAttributes';

import type { IndexedParticipantAttribute } from '../participantAttributes';
import type { User } from '@/types/directory/User';
import type { ObjectiveParticipantAttribute } from '@/types/objectives';

import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

interface ObjectiveSelectedParticipantsProps {
    employees: { user: User; isRemovable: boolean }[];
    onRemoveEmployee: (employeeId: string) => void;
    /** Empty until the company has answered: a value is never named from a company not yet read. */
    attributes: { attribute: ObjectiveParticipantAttribute; employeeCount: number | undefined }[];
    attributesByKey: ReadonlyMap<string, IndexedParticipantAttribute> | undefined;
    /** Absent to a reader who may not pick values: their chips are then read-only. */
    onRemoveAttribute?: (attribute: ObjectiveParticipantAttribute) => void;
}

/** The chips standing in the field: the people picked, then the company values whose members
 * take part. */
export default function ObjectiveSelectedParticipants({
    employees,
    onRemoveEmployee,
    attributes,
    attributesByKey,
    onRemoveAttribute,
}: ObjectiveSelectedParticipantsProps) {
    const { t } = useTranslation();

    return (
        <>
            {employees.map(({ user, isRemovable }) => (
                <ObjectiveParticipantChip
                    key={user.id}
                    leadingInset={'avatar'}
                    leading={
                        // One avatar size below the result list: the avatar sets the field's
                        // height, `xs` is too tall beside 12px text, and the next size down would
                        // be far too small.
                        <Avatar
                            src={getProfilePictureUrl(user.profilePictureUrl)}
                            alt={getFullName(user)}
                            initials={getInitials(user)}
                            size={'2xs'}
                        />
                    }
                    name={getFullName(user)}
                    removal={
                        isRemovable
                            ? {
                                  label: t('objectives.form.removeEmployee'),
                                  onRemove: () => onRemoveEmployee(user.id),
                                  dataTestId: `objective-employee-remove-${user.id}`,
                              }
                            : undefined
                    }
                    dataTestId={`objective-employee-selected-${user.id}`}
                />
            ))}

            {attributes.map(({ attribute, employeeCount }) => (
                <ObjectiveAttributeChip
                    key={participantAttributeKey(attribute)}
                    attribute={attribute}
                    attributesByKey={attributesByKey}
                    employeeCount={employeeCount}
                    onRemove={onRemoveAttribute && (() => onRemoveAttribute(attribute))}
                />
            ))}
        </>
    );
}
